/**
 * Catalog of the return logistics choices offered on the practice intake form.
 *
 * This is the single source of truth for the option set. Two consumers read it:
 *  - src/Pages/PracticeIntakeForm.jsx renders the enabled options as the
 *    "Return Logistics" dropdown (via buildReturnLogisticsOptions).
 *  - src/config/practiceControls.js turns the same list into one admin toggle
 *    per option, so a practice can hide the ones it doesn't offer.
 *
 * `value` is what gets written to the case and is branched on downstream (the
 * intake form reveals the shipping/clinic fields based on it, and the backend
 * and PDFs key off the same strings) — these must not change. `key` is the
 * Firestore settings key, kept separate from the wording so labels can be
 * reworded without orphaning stored settings.
 *
 * Toggles use the same opt-out model as the email controls: a missing value
 * means enabled, so practices that never open their settings keep seeing every
 * option.
 */

// Firestore sub-map these toggles live under: settings.returnLogistics.*
export const RETURN_LOGISTICS_STORAGE_KEY = "returnLogistics";

export const RETURN_LOGISTICS_DEFAULT_VALUE = true;

export const RETURN_LOGISTICS_OPTIONS = [
  {
    key: "returnToPrimaryCareClinic",
    value: "Return to primary care clinic",
    adminLabel: "Return to primary care clinic",
    adminDescription:
      "Remains are returned to the pet's primary care clinic. Asks for the clinic's name and address.",
    disabled: true,
    getLabel: ({ brand }) => `${brand} return to primary care clinic`,
  },
  {
    key: "shipToPetFamily",
    value: "Ship to pet family",
    adminLabel: "Ship to pet family",
    adminDescription:
      "Remains are shipped to the pet family. Asks for a shipping address.",
    disabled: true,
    getLabel: ({ brand }) => `${brand} ship to pet family`,
  },
  {
    key: "pickUpFromRestingWaters",
    value: "Pick up from Resting Waters",
    adminLabel: "Pet family pick up from Resting Waters",
    adminDescription:
      "The pet family collects the remains from Resting Waters themselves.",
    disabled: true,
    getLabel: () => "Pet family pick up from Resting Waters",
  },
  {
    key: "returnToPractice",
    value: "Return to practice",
    adminLabel: "Return to practice",
    adminDescription:
      "Remains are returned to your practice for the pet family to collect.",
    disabled: true,
    getLabel: ({ brand, practiceName }) => `${brand} return to ${practiceName}`,
  },
];

/**
 * Whether a single option is enabled for a practice.
 *
 * @param {Object|undefined} settings - The practice's `settings` map.
 * @param {Object} option - An option from RETURN_LOGISTICS_OPTIONS.
 * @returns {boolean}
 */
export function isReturnLogisticsOptionEnabled(settings, option) {
  const stored = settings?.[RETURN_LOGISTICS_STORAGE_KEY]?.[option.key];
  return stored === undefined ? RETURN_LOGISTICS_DEFAULT_VALUE : stored;
}

/**
 * Builds the enabled dropdown options for a practice, in catalog order.
 *
 * @param {Object|undefined} practiceFields - `practice.fields` from usePractice.
 * @param {Object|undefined} settings - The practice's `settings` map.
 * @returns {Array<{label: string, value: string}>} Possibly empty, when an admin
 *   has turned every option off.
 */
export function buildReturnLogisticsOptions(practiceFields, settings) {
  const brand = practiceFields?.brand || "Evvi";
  const practiceName = practiceFields?.practiceName || "practice";

  return RETURN_LOGISTICS_OPTIONS.filter((option) =>
    isReturnLogisticsOptionEnabled(settings, option)
  ).map((option) => ({
    label: option.getLabel({ brand, practiceName }),
    value: option.value,
  }));
}
